/** @typedef {{ optionId?: string, label: string, price: number, discountedPrice: number, quantity: number }} VariantOption */

function toPriceNum(value) {
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
}

/**
 * @param {unknown} product
 * @returns {VariantOption[]}
 */
export function getVariantOptions(product) {
  const g = product?.variantGroup;
  if (!g || !Array.isArray(g.options)) return [];
  return g.options.filter(
    (o) =>
      o &&
      String(o.label || "").trim() &&
      Number.isFinite(Number(o.discountedPrice ?? o.price)),
  );
}

/**
 * @param {unknown} product
 * @returns {boolean}
 */
export function productHasVariantOptions(product) {
  return getVariantOptions(product).length > 0;
}

/**
 * @param {unknown} product
 * @param {string|undefined|null} variantOptionId
 * @returns {VariantOption | null}
 */
export function findVariantOption(product, variantOptionId) {
  if (variantOptionId == null || variantOptionId === "") return null;
  const id = String(variantOptionId);
  return getVariantOptions(product).find((o) => String(o.optionId) === id) ?? null;
}

/**
 * @param {unknown} product
 * @param {string|undefined|null} variantOptionId
 */
export function getLineItemUnitPrice(product, variantOptionId) {
  const opt = findVariantOption(product, variantOptionId);
  if (opt) {
    const list = toPriceNum(opt.price);
    const dp = toPriceNum(opt.discountedPrice);
    if (list != null && dp != null && dp < list) return dp;
    const productList = toPriceNum(product?.price);
    const productSale = toPriceNum(
      product?.discountedPrice ?? product?.discountPrice,
    );
    if (
      list != null &&
      productList != null &&
      productSale != null &&
      productSale < productList &&
      productList === list
    ) {
      return productSale;
    }
    if (dp != null) return dp;
    return list ?? 0;
  }
  const sale = toPriceNum(
    product?.discountedPrice ?? product?.discountPrice ?? product?.price,
  );
  return sale ?? 0;
}

/**
 * @param {unknown} product
 * @param {string|undefined|null} variantOptionId
 */
export function getLineItemListPrice(product, variantOptionId) {
  const opt = findVariantOption(product, variantOptionId);
  if (opt) {
    const p = toPriceNum(opt.price);
    if (p != null) return p;
  }
  const base = toPriceNum(product?.price);
  if (base != null) return base;
  return getLineItemUnitPrice(product, variantOptionId);
}

/**
 * Max units for this line. Variant products without a selected option → 0 (cannot add).
 * @param {unknown} product
 * @param {string|undefined|null} variantOptionId
 */
export function getProductMaxCartQuantity(product, variantOptionId) {
  if (!product || typeof product !== "object") return Infinity;
  const opt = findVariantOption(product, variantOptionId);
  if (opt) {
    const n = Number(opt.quantity);
    if (!Number.isFinite(n)) return Infinity;
    return Math.max(0, Math.floor(n));
  }
  if (productHasVariantOptions(product)) return 0;
  const raw = product.quantity ?? product.stock;
  if (raw === undefined || raw === null || raw === "") return Infinity;
  const n = Number(raw);
  if (!Number.isFinite(n)) return Infinity;
  return Math.max(0, Math.floor(n));
}

/** Lowest sale price among options (for cards / "from"). */
export function getVariantMinSalePrice(product) {
  const opts = getVariantOptions(product);
  if (!opts.length) return null;
  const prices = opts
    .map((o) => toPriceNum(o.discountedPrice ?? o.price))
    .filter((n) => n != null);
  return prices.length ? Math.min(...prices) : null;
}

/**
 * Storefront sale + list (MRP) for cards and listings.
 * Uses variant option prices and product-level sale/MRP (whichever gives the best customer price).
 */
export function getStorefrontPrices(product) {
  const rows = [];

  const productList = toPriceNum(product?.price);
  const productSale = toPriceNum(
    product?.discountedPrice ?? product?.discountPrice,
  );
  if (productList != null && productSale != null) {
    rows.push({ sale: productSale, list: productList });
  }

  for (const o of getVariantOptions(product)) {
    const list = toPriceNum(o.price);
    const sale = toPriceNum(o.discountedPrice ?? o.price);
    if (list != null && sale != null) rows.push({ sale, list });
  }

  if (!rows.length) {
    return { salePrice: 0, listPrice: 0, hasDiscount: false };
  }

  const salePrice = Math.min(...rows.map((r) => r.sale));
  const tied = rows.filter((r) => r.sale === salePrice);
  const listPrice = Math.max(...tied.map((r) => r.list));
  const hasDiscount = listPrice > salePrice && listPrice > 0;

  return { salePrice, listPrice, hasDiscount };
}

/** Highest list (MRP) among all options — do not mix with {@link getVariantMinSalePrice} for one discount %. */
export function getVariantMaxListPrice(product) {
  const opts = getVariantOptions(product);
  if (!opts.length) return null;
  const prices = opts.map((o) => Number(o.price)).filter(Number.isFinite);
  return prices.length ? Math.max(...prices) : null;
}

/**
 * MRP (`price`) tied to the lowest sale among options — same variant row as "From ₹…".
 * Prevents showing min sale next to max MRP (misleading discount %).
 */
export function getVariantListPriceAtMinSale(product) {
  const { listPrice } = getStorefrontPrices(product);
  return listPrice > 0 ? listPrice : null;
}

export function cartLineKey(productId, variantOptionId) {
  return `${String(productId)}::${variantOptionId || ""}`;
}

/** Sum of option quantities (in-stock signal for cards). */
export function getTotalVariantStock(product) {
  return getVariantOptions(product).reduce(
    (s, o) => s + Math.max(0, Math.floor(Number(o.quantity) || 0)),
    0
  );
}

/** Stable `/products/...` segment — Mongo `_id` so shared URLs resolve without navigation state. */
export function productDetailPathSegment(product) {
  const id = product?._id ?? product?.id;
  return id != null ? String(id) : "";
}
